import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorScreen } from '@/components/ui/ErrorScreen'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

// Catches render errors from whatever page is currently mounted inside
// AppLayout's <Outlet />, so a single broken page doesn't take the sidebar
// and topbar down with it. Give this a `key` that changes on navigation
// (see AppLayout) so a crash on one page doesn't linger after the user
// navigates away.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } })
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorScreen
          message="An unexpected error occurred while displaying this page."
          onRetry={() => this.setState({ error: null })}
        />
      )
    }
    return this.props.children
  }
}
